import type { ApolloError } from '@apollo/client';
import type { GetSessionDataQuery } from '@predium/client-graphql';
import { language_enum, org_sector_type_enum, org_status_type_enum, org_type_enum, role_enum } from '@predium/enums';
import { createStoreWithProducer } from '@xstate/store-react';
import { produce } from 'immer';
import isNil from 'lodash/isNil';
import { faro } from '@/faro';
import { updateTracingUserAttributes } from '@/tracing';

type SessionData = GetSessionDataQuery['getSessionData'];

/**
 * Mirrors `isFeatureEnabled()` in
 * apps/backend/src/resolvers/featureFlags/featureFlags.resolver.ts.
 * True in development or sandbox builds.
 */
const isFeatureEnabled = (): boolean =>
  import.meta.env.MODE === 'development' || import.meta.env.VITE_SANDBOX === 'true';

/**
 * The keys define the canonical set of feature flags (and the `FeatureFlagName` type).
 * The values mirror `featureFlagFallback` in
 * apps/backend/src/resolvers/featureFlags/featureFlags.resolver.ts purely as documentation —
 * the backend is authoritative and already applies these defaults, so they are NOT re-applied
 * client-side as a fallback (see `expandFeatureFlags`).
 */
export const ALL_FEATURE_FLAGS = {
  ACTION_PLANNING_MENU_OPTION_ENABLED: isFeatureEnabled(),
  ACTION_PLAN_BULK_DELETION: isFeatureEnabled(),
  ACTION_PLAN_PORTFOLIO_MISMATCH_HIGHLIGHT: isFeatureEnabled(),
  AUTOMATIC_ACTION_COST_CALCULATION_UPDATES_ENABLED: isFeatureEnabled(),
  AUTO_DEACTIVATE_INACTIVE_USERS: true,
  BATCH_ESG_ANALYSIS_PDF_EXPORT: isFeatureEnabled(),
  BE_COUNTRY_SUPPORT: isFeatureEnabled(),
  BUILDING_CREATION_CONSUMER_EXTENSION: false,
  BUILDING_CREATION_EPC_LIMITLESS: isFeatureEnabled(),
  BUILDING_MODEL_DRAFT: isFeatureEnabled(),
  BUILDING_MODEL_HISTORY: isFeatureEnabled(),
  BUILDING_MODEL_VERSIONS_ANALYSIS: false,
  CBI_ENABLED: isFeatureEnabled(),
  CLIENT_CACHE_AND_NETWORK_POLICY: false,
  CLIMATE_RISK_V2: isFeatureEnabled(),
  CLIMATE_RISK_V2_MOCK: isFeatureEnabled(),
  CO2_ABATEMENT_AND_PAYBACK_PERIOD_KPIS: isFeatureEnabled(),
  COMPARE_WITH_USE_TYPE_OPTIONS: isFeatureEnabled(),
  CONFIGURABLE_CO2_PRICE: isFeatureEnabled(),
  CONSUMPTION_HUB: isFeatureEnabled(),
  CONSUMPTION_PORTFOLIO_ANALYSIS: isFeatureEnabled(),
  CONSUMPTION_STATUS_QUO_TAB_ENABLED: isFeatureEnabled(),
  CUSTOMIZABLE_CALCULATIONS: isFeatureEnabled(),
  CUSTOM_EXCEL_EXPORT: isFeatureEnabled(),
  CUSTOM_PROPERTIES: isFeatureEnabled(),
  CUSTOM_PROPERTY_EDITING: isFeatureEnabled(),
  CUSTOM_TARGETS: isFeatureEnabled(),
  CZ_COUNTRY_SUPPORT: isFeatureEnabled(),
  DISABLE_NON_RESIDENTIAL_EFFICIENCY_CLASSES: false,
  DRIVE_BULK_CREATE_BUILDINGS: isFeatureEnabled(),
  ENABLE_BUILDING_ADDRESS_RANGE_SELECTION: isFeatureEnabled(),
  ENABLE_DECARBONISATION_PATH_IN_ACTION_PLANNING: isFeatureEnabled(),
  ENABLE_DREES_UND_SOMMER: isFeatureEnabled(),
  ENABLE_SHAREABLE_BUILDING_STATUS_QUO: isFeatureEnabled(),
  ENABLE_STATUS_QUO_MIRRORING_EXPORT: isFeatureEnabled(),
  EU_TAXONOMY_DATASOURCE: isFeatureEnabled(),
  EXPERIMENTAL_BULK_ACTION_FEATURES: isFeatureEnabled(),
  FEATURE_EDITABLE_BULK_ACTIONS: isFeatureEnabled(),
  FRANCE_COUNTRY_SUPPORT: false,
  GLB_MODEL_ACCESS: isFeatureEnabled(),
  HEATING_DEMAND_EDITABLE: isFeatureEnabled(),
  HIDE_CBI_INTERNAL_COMMENTS: isFeatureEnabled(),
  HIDE_CBI_INVOICE_FIELDS: isFeatureEnabled(),
  KYSELY_ANALYZE_SCENARIO: false,
  LOGIN_HISTORY: isFeatureEnabled(),
  M2_ABSOLUTE_VALUE_SWITCHER_FOR_ACTION_PLANNING: isFeatureEnabled(),
  MAPS_PROXY: false,
  MULTI_SELECT_IN_DRIVE_FEATURE: false,
  NEW_BUILDINGS_TABLE: isFeatureEnabled(),
  NEW_CONSTRUCTION: isFeatureEnabled(),
  NO_COMPARISON_BENCHMARK: isFeatureEnabled(),
  OUT_OF_RANGE_GRADIENT_DESCENT_SCALING: false,
  RECOMMENDED_ACTIONS: isFeatureEnabled(),
  REPORTING_MENU_OPTION_ENABLED: isFeatureEnabled(),
  SCOPE_PERSPECTIVE_SELECTOR: isFeatureEnabled(),
  SHOW_ORG_LOGO_IN_SIDEBAR: isFeatureEnabled(),
  SLF_CHECK_COMPLIANT: isFeatureEnabled(),
  UK_COUNTRY_SUPPORT: isFeatureEnabled(),
  USER_MANAGEMENT_CREATE: isFeatureEnabled(),
  USE_CUSTOM_TARGET_PATH: false,
  ARBEITSHILFE_85_EMISSION_FACTORS: isFeatureEnabled(),
} satisfies Record<string, boolean>;

export type FeatureFlagName = keyof typeof ALL_FEATURE_FLAGS;
export type ServerSideFlags = Record<FeatureFlagName, boolean>;

export function expandFeatureFlags(enabled: readonly string[]): ServerSideFlags {
  const set = new Set(enabled);
  return Object.fromEntries(
    (Object.keys(ALL_FEATURE_FLAGS) as FeatureFlagName[]).map((name) => [name, set.has(name)]),
  ) as ServerSideFlags;
}

type SessionStatus = 'loading' | 'unauthenticated' | 'error' | 'authenticated';

type SessionContext = {
  status: SessionStatus;
  error: ApolloError | null;
  user: SessionData['user'] | null;
  org: SessionData['org'] | null;
  featureFlags: ServerSideFlags | null;
  isAdmin: boolean;
  hasPortfolioWithWriteAccess: boolean;
  firstBuildingId: number | null;
  playgroundFlags: Partial<ServerSideFlags> | null;
  pendingBulkActionIds: number[];
};

const initialContext: SessionContext = {
  status: 'loading',
  error: null,
  user: null,
  org: null,
  featureFlags: null,
  isAdmin: false,
  hasPortfolioWithWriteAccess: false,
  firstBuildingId: null,
  playgroundFlags: null,
  pendingBulkActionIds: [],
};

export const sessionStore = createStoreWithProducer(produce, {
  context: initialContext,
  on: {
    hydrate: (ctx, event: { data: SessionData }) => {
      ctx.status = 'authenticated';
      ctx.error = null;
      ctx.user = event.data.user;
      ctx.org = event.data.org;
      ctx.featureFlags = expandFeatureFlags(event.data.enabledFeatureFlags);
      ctx.isAdmin = event.data.isAdmin;
      ctx.hasPortfolioWithWriteAccess = event.data.hasPortfolioWithWriteAccess;
      ctx.firstBuildingId = event.data.firstBuildingId ?? null;

      const userId = event.data.user.id;
      const orgId = event.data.org.id;

      updateTracingUserAttributes(userId, orgId);
      faro?.api?.setUser({
        id: String(userId),
        attributes: { orgId: String(orgId) },
      });
    },
    hydrateAnonymous: (
      ctx,
      event: {
        snapshot?: {
          language?: language_enum;
          orgTypeId?: org_type_enum;
          orgSectorTypeId?: org_sector_type_enum;
          enabledFeatureFlags?: string[];
        };
      },
    ) => {
      const snap = event.snapshot;
      ctx.status = 'authenticated';
      ctx.error = null;
      ctx.user = {
        id: 0,
        email: null,
        firstName: '',
        lastName: '',
        createdAt: new Date(0).toISOString(),
        language: snap?.language ?? language_enum.enGB,
        roleDescription: '',
        isActive: false,
        role: role_enum.DANGER_PUBLIC_ROLE,
      };
      ctx.org = {
        id: 0,
        logoUrl: null,
        name: '',
        orgTypeId: snap?.orgTypeId ?? org_type_enum.CUSTOMER,
        statusTypeId: org_status_type_enum.ACTIVE,
        userLogoutIdleTimeout: null,
        isEconomicUnitsToggled: false,
        orgSector: snap?.orgSectorTypeId ?? org_sector_type_enum.STANDARD,
      };
      ctx.featureFlags = expandFeatureFlags(snap?.enabledFeatureFlags ?? []);
      ctx.isAdmin = false;
      ctx.hasPortfolioWithWriteAccess = false;
      ctx.firstBuildingId = null;
    },
    setUnauthenticated: (ctx) => {
      ctx.status = 'unauthenticated';
      ctx.error = null;
      ctx.user = null;
      ctx.org = null;
      ctx.featureFlags = null;
      ctx.isAdmin = false;
      ctx.hasPortfolioWithWriteAccess = false;
      ctx.firstBuildingId = null;
      ctx.playgroundFlags = null;
      ctx.pendingBulkActionIds = [];

      updateTracingUserAttributes(null, null);
      faro?.api?.resetUser();
    },
    setError: (ctx, event: { error: ApolloError }) => {
      if (ctx.status === 'authenticated') return;
      ctx.status = 'error';
      ctx.error = event.error;
    },
    setPlaygroundFlags: (ctx, event: { flags: Partial<ServerSideFlags> | null }) => {
      if (ctx.status !== 'authenticated') return;
      ctx.playgroundFlags = event.flags && Object.keys(event.flags).length > 0 ? event.flags : null;
    },
    setIsEconomicUnitsToggled: (ctx, event: { value: boolean }) => {
      if (ctx.status !== 'authenticated' || isNil(ctx.org)) return;
      ctx.org.isEconomicUnitsToggled = event.value;
    },
    registerPendingBulkAction: (ctx, event: { id: number }) => {
      if (!ctx.pendingBulkActionIds.includes(event.id)) {
        ctx.pendingBulkActionIds.push(event.id);
      }
    },
    dismissPendingBulkAction: (ctx, event: { id: number }) => {
      ctx.pendingBulkActionIds = ctx.pendingBulkActionIds.filter((id) => id !== event.id);
    },
  },
});
